import { Link } from '@tanstack/react-router';
import type { CalendarDayCell, ChannexChannelResource, PropertyResponse } from '@/shared/api';
import { ChevronRight } from 'lucide-react';
import { formatMoney } from '@/shared/lib/format';
import { OccupancyStrip } from '../OccupancyStrip';
import { OtaBadges } from '../OtaBadges';
import { CoverThumb } from './CoverThumb';
import { ListStatusPill } from './ListStatusPill';

interface PropertyCardProps {
  property: PropertyResponse;
  occupancyCells: CalendarDayCell[] | undefined;
  stripFrom: string;
  channels: ChannexChannelResource[];
  listContext: Record<string, unknown>;
}

/**
 * Die Apartmentzeile als gestapelte Karte — die Komposition unterhalb der Tabellenbreite.
 *
 * <p>Sechs Spalten passen nicht in 360 px; die Tabelle lag dort hinter einem horizontalen Scroll
 * und ausgerechnet Zustand und Kanäle standen ganz rechts. Genau ein Aktivierungsweg pro Karte
 * (§6): der Link liegt über Bild und Namen, alles andere ist Auskunft.</p>
 */
export function PropertyCard({
  property: p,
  occupancyCells,
  stripFrom,
  channels,
  listContext,
}: PropertyCardProps) {
  return (
    <li className="border-line bg-card shadow-card rounded-lg border p-4">
      <div className="flex items-start justify-between gap-3">
        <Link
          to="/properties/$id"
          params={{ id: p.id }}
          search={listContext as never}
          className="hover:text-sage flex min-w-0 flex-1 items-start gap-3 transition-colors"
        >
          <CoverThumb property={p} />
          <span className="min-w-0">
            <span className="text-ink block font-serif text-lg">
              {p.adminDisplayName ?? p.name}
            </span>
            {p.adminDisplayName && p.adminDisplayName !== p.name && (
              <span className="text-ink-faint mt-0.5 block truncate text-xs">Intern: {p.name}</span>
            )}
            <span className="text-ink-soft mt-0.5 block text-xs">
              {p.address.street ? `${p.address.street}, ` : ''}
              {p.address.city} · {p.address.countryCode}
            </span>
          </span>
          <ChevronRight size={18} className="text-navy mt-1 shrink-0" aria-hidden />
        </Link>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
        <ListStatusPill status={p.status} />
        <OtaBadges
          channexPropertyId={p.channexPropertyId}
          unitPublished={p.channexUnitPublished}
          publicationHealth={p.publicationHealth}
          channels={channels}
        />
      </div>

      <dl className="border-line/50 mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t pt-3 text-xs">
        <div className="min-w-0">
          <dt className="text-ink-faint text-[11px] uppercase tracking-wide">Belegung · 14 Tage</dt>
          <dd className="mt-1">
            <OccupancyStrip cells={occupancyCells} fromDate={stripFrom} />
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-ink-faint text-[11px] uppercase tracking-wide">Reinigungsgebühr</dt>
          <dd className="text-ink mt-1 tabular-nums">{formatMoney(p.cleaningFee, p.currency)}</dd>
        </div>
      </dl>
    </li>
  );
}
